import { useState, useRef, useEffect } from "react";
import { Flag, Check, X, Trash2 } from "lucide-react";
import { TooltipWrapper } from "../../../components/ui/Tooltip";
import { useAuthStore } from "../../../stores/authStore";

interface PrioridadeEditorProps {
  chamadoId: number;
  prioridade: number | null;
  onSave: (id: number, prioridade: number | null) => void;
  isPending?: boolean;
  /** Variante compacta para o card do kanban, ou expandida para o modal */
  variant?: "badge" | "detail";
}

export const PrioridadeEditor = ({
  chamadoId,
  prioridade,
  onSave,
  isPending = false,
  variant = "badge",
}: PrioridadeEditorProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const equipe = useAuthStore((state) => Boolean(state.user?.equipe));
  const [value, setValue] = useState(prioridade?.toString() ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      setValue(prioridade?.toString() ?? "");
      // Timeout para garantir que o input está renderizado
      setTimeout(() => inputRef.current?.select(), 0);
    }
  }, [isEditing, prioridade]);

  const handleSave = () => {
    const trimmed = value.trim();
    const newPrioridade = trimmed === "" ? null : parseInt(trimmed, 10);

    if (trimmed !== "" && isNaN(newPrioridade as number)) {
      setIsEditing(false);
      return;
    }

    if (newPrioridade !== prioridade) {
      onSave(chamadoId, newPrioridade);
    }
    setIsEditing(false);
  };

  const handleRemove = () => {
    if (prioridade !== null) {
      onSave(chamadoId, null);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // A prioridade é a ordem do cliente: a equipe T4i vê, mas não edita (a API recusa).
    if (!isPending && !equipe) {
      setIsEditing(true);
    }
  };

  // --- Modo edição ---
  if (isEditing) {
    return (
      <div
        className="inline-flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          className={`border border-blue-400 rounded-md text-center focus:outline-none focus:ring-2 focus:ring-blue-300 ${
            variant === "badge"
              ? "w-12 h-6 text-xs"
              : "w-16 h-7 text-sm"
          }`}
          placeholder="#"
        />
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            handleSave();
          }}
          className="p-0.5 text-green-600 hover:bg-green-100 rounded"
          title="Salvar"
        >
          <Check className="w-3.5 h-3.5" />
        </button>
        {prioridade != null && (
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              handleRemove();
            }}
            className="p-0.5 text-red-500 hover:bg-red-100 rounded"
            title="Remover prioridade"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            setIsEditing(false);
          }}
          className="p-0.5 text-gray-400 hover:bg-gray-100 rounded"
          title="Cancelar"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // --- Modo visualização (badge) ---
  if (variant === "badge") {
    if (prioridade != null) {
      return (
        <TooltipWrapper
          tooltip="Clique para alterar a prioridade"
          placement="bottom"
        >
          <div
            onClick={handleClick}
            className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold rounded-lg border shadow-sm bg-gradient-to-r from-slate-100 to-slate-50 text-slate-800 border-slate-200 cursor-pointer hover:border-blue-300 hover:shadow-md transition-all ${
              isPending ? "opacity-60 pointer-events-none" : ""
            }`}
          >
            <Flag className="w-3 h-3 text-slate-600" />
            <span>{prioridade}</span>
          </div>
        </TooltipWrapper>
      );
    }

    return (
      <TooltipWrapper
        tooltip="Clique para definir a prioridade"
        placement="bottom"
      >
        <div
          onClick={handleClick}
          className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-lg border border-dashed border-amber-300 bg-amber-50 text-amber-600 cursor-pointer hover:border-amber-400 hover:bg-amber-100 transition-all ${
            isPending ? "opacity-60 pointer-events-none" : ""
          }`}
        >
          <Flag className="w-3 h-3 text-amber-400" />
          <span>S/ prioridade</span>
        </div>
      </TooltipWrapper>
    );
  }

  // --- Modo visualização (detail - usado no modal) ---
  return (
    <span
      onClick={handleClick}
      className={`ml-2 cursor-pointer hover:text-blue-600 transition-colors ${
        prioridade != null ? "" : "text-amber-600 italic"
      } ${isPending ? "opacity-60 pointer-events-none" : ""}`}
      title="Clique para alterar"
    >
      {prioridade != null ? prioridade : "Não definida"}
    </span>
  );
};
